export interface HistoryTask {
  id: number
  label: string
  undo: () => Promise<boolean> | boolean
  redo: () => Promise<boolean> | boolean
}

/**
 * A change the grid did not make itself but can still undo.
 *
 * Structural edits — insert/delete row, insert/delete column — are performed by the
 * page that owns the data, so only that page knows how to reverse them. It hands one
 * of these back and the grid puts it on the same stack as a cell edit.
 */
export interface GridReversible {
  /** Shown in the history menu, already translated. */
  label: string;
  undo: () => Promise<boolean> | boolean;
  redo: () => Promise<boolean> | boolean;
}

/** A change made outside the grid, handed in through the `historyEntry` prop; a new `id` is a new entry. */
export interface GridHistoryEntry extends GridReversible {
  readonly id: number;
}

export interface HistoryState {
  undo: HistoryTask[]
  redo: HistoryTask[]
}

export const EMPTY_HISTORY: HistoryState = { undo: [], redo: [] }

export function pushHistoryTask(state: HistoryState, task: HistoryTask): HistoryState {
  return { undo: [...state.undo, task], redo: [] }
}

export function menuTasks(tasks: readonly HistoryTask[]): HistoryTask[] {
  return [...tasks].reverse()
}

export function tasksThroughNewest(tasks: readonly HistoryTask[], menuIndex: number): HistoryTask[] {
  if (menuIndex < 0) return []
  return menuTasks(tasks).slice(0, menuIndex + 1)
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

/**
 * A stored setting moved from `from` to `to` again, on top of what it holds now.
 * Only the parts that differ between the two are written, at the deepest level
 * they differ, so another grid's (or another column's) later change survives an
 * undo of this one.
 */
export function replaySettingChange(current: unknown, from: unknown, to: unknown): unknown {
  if (!isPlainObject(from) || !isPlainObject(to) || !isPlainObject(current)) return to
  const next: Record<string, unknown> = { ...current }
  for (const key of new Set([...Object.keys(from), ...Object.keys(to)])) {
    if (JSON.stringify(from[key]) === JSON.stringify(to[key])) continue
    if (to[key] === undefined) delete next[key]
    else next[key] = replaySettingChange(current[key], from[key], to[key])
  }
  return next
}

export function historyValueMatches(actual: unknown, expected: unknown): boolean {
  if (Object.is(actual, expected)) return true
  // A cleared cell is recorded as '' but may come back from the save as null or undefined,
  // and numbers may round-trip as their string form; both are the same value to the user.
  const isEmpty = (value: unknown) => value === null || value === undefined || value === ''
  if (isEmpty(actual) || isEmpty(expected)) return isEmpty(actual) && isEmpty(expected)
  if (typeof actual === 'number' || typeof expected === 'number') {
    return Number(actual) === Number(expected)
  }
  return false
}
